<template>
  <div class="popup z-index-10-fg confirmation small-popup assign-salesperson-popup">
    <div class="popup-content">
      <div class="popup-header">
        <h2>Assign Lead</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>
      <div class="popup-body">
        <Dropdown :options="salespeopleList" label="Salesperson" v-model="salesperson" class="input-item medium-item" />
        <p class="location-note" v-if="lead?.SUGGESTEDSALESPERSON">
          The zip code of this lead is <span class="link" @click="navigateToAddress(lead.SUGGESTEDSALESPERSON)">{{ lead.SUGGESTEDSALESPERSON.zip }}</span> ({{ lead.SUGGESTEDSALESPERSON.city }},
          {{ convertToAbbreviation(lead.SUGGESTEDSALESPERSON.state) }}).
        </p>
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">close</button>
        <button :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="updateSalesperson()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import Dropdown from './inputs/Dropdown.vue';
import useLeadsQuery from '@/composables/useLeadsQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import useAlerts from '@/composables/useAlerts';
import useLeadMutate from '@/composables/useLeadMutate';
import useReminderMutate from '@/composables/useReminderMutate';
import useCurrentUser from '@/composables/useCurrentUser';

export default {
  name: 'AssignSalesperson',
  emits: ['closePopup'],
  components: {
    Dropdown,
  },
  props: {
    leadid: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    const { updateLead } = useLeadMutate();
    const { addReminder } = useReminderMutate();
    const { user } = useCurrentUser();
    const { salespeople } = useUsersQuery();

    return {
      leads: useLeadsQuery.leadsIncludingSingleLead,
      salespeople,
      user,
      addAlert,
      updateLead,
      addReminder,
    };
  },
  data() {
    return {
      salesperson: null,
      isSaving: false,
    };
  },
  computed: {
    salespeopleList() {
      return this.salespeople.map((salesperson) => {
        if (this.lead?.SUGGESTEDSALESPERSON?.salespersonid === salesperson.USERID) {
          return { value: salesperson.USERID, text: `${salesperson.DISPLAYNAME} (Suggested)` };
        }
        return { value: salesperson.USERID, text: salesperson.DISPLAYNAME };
      });
    },
    lead() {
      return this.leads?.find((lead) => lead.LEADID === this.leadid) || null;
    },
  },
  methods: {
    async updateSalesperson() {
      this.isSaving = true;
      if (!this.lead) {
        this.addAlert('Error saving salesperson', 'high', 4);
        this.$emit('closePopup');
        return;
      }

      if (!this.salesperson) {
        this.addAlert('Error saving salesperson', 'high', 4);
        this.$emit('closePopup');
        return;
      }

      const updatedLead = { ASSIGNEDID: this.salesperson };
      await this.updateLead(this.lead.LEADID, updatedLead);

      this.isSaving = false;
      this.addAlert('Assigned salesperson', 'low', 3);

      let leadName = this.lead.NAME;
      if (this.lead.company) {
        leadName = this.lead.company.NAME;
      } else if (this.lead.contact) {
        leadName = this.lead.contact.FIRSTNAME + ' ' + this.lead.contact.LASTNAME;
      }

      if (this.salesperson != this.user.userid) {
        this.addReminder({
          TYPE: `Assigned Lead`,
          ASSIGNEDID: this.salesperson,
          CONTACTID: null,
          COMPANYID: null,
          LEADID: this.lead.LEADID,
          COMPLETED: false,
          TEXT: `You have been assigned a lead for ${leadName}.`,
          DATE: new Date().toISOString(),
        });
      }

      // the lead may no longer be accessible after the update.
      this.lead.ASSIGNEDID = this.salesperson;

      this.$emit('closePopup');
    },
    navigateToAddress(sugSalPerObj) {
      window.open(`https://www.google.com/maps/search/${sugSalPerObj.city}, ${sugSalPerObj.state} ${sugSalPerObj.zip}`, '_blank');
    },
    convertToAbbreviation(state) {
      switch (state) {
        case 'Alabama':
          return 'AL';
        case 'Alaska':
          return 'AK';
        case 'Arizona':
          return 'AZ';
        case 'Arkansas':
          return 'AR';
        case 'California':
          return 'CA';
        case 'Colorado':
          return 'CO';
        case 'Connecticut':
          return 'CT';
        case 'Delaware':
          return 'DE';
        case 'Florida':
          return 'FL';
        case 'Georgia':
          return 'GA';
        case 'Hawaii':
          return 'HI';
        case 'Idaho':
          return 'ID';
        case 'Illinois':
          return 'IL';
        case 'Indiana':
          return 'IN';
        case 'Iowa':
          return 'IA';
        case 'Kansas':
          return 'KS';
        case 'Kentucky':
          return 'KY';
        case 'Louisiana':
          return 'LA';
        case 'Maine':
          return 'ME';
        case 'Maryland':
          return 'MD';
        case 'Massachusetts':
          return 'MA';
        case 'Michigan':
          return 'MI';
        case 'Minnesota':
          return 'MN';
        case 'Mississippi':
          return 'MS';
        case 'Missouri':
          return 'MO';
        case 'Montana':
          return 'MT';
        case 'Nebraska':
          return 'NE';
        case 'Nevada':
          return 'NV';
        case 'New Hampshire':
          return 'NH';
        case 'New Jersey':
          return 'NJ';
        case 'New Mexico':
          return 'NM';
        case 'New York':
          return 'NY';
        case 'North Carolina':
          return 'NC';
        case 'North Dakota':
          return 'ND';
        case 'Ohio':
          return 'OH';
        case 'Oklahoma':
          return 'OK';
        case 'Oregon':
          return 'OR';
        case 'Pennsylvania':
          return 'PA';
        case 'Rhode Island':
          return 'RI';
        case 'South Carolina':
          return 'SC';
        case 'South Dakota':
          return 'SD';
        case 'Tennessee':
          return 'TN';
        case 'Texas':
          return 'TX';
        case 'Utah':
          return 'UT';
        case 'Vermont':
          return 'VT';
        case 'Virginia':
          return 'VA';
        case 'Washington':
          return 'WA';
        case 'West Virginia':
          return 'WV';
        case 'Wisconsin':
          return 'WI';
        case 'Wyoming':
          return 'WY';
        case 'District of Columbia':
          return 'DC';
        case 'Puerto Rico':
          return 'PR';
        case 'Guam':
          return 'GU';
        case 'American Samoa':
          return 'AS';
        case 'U.S. Virgin Islands':
          return 'VI';
        case 'Northern Mariana Islands':
          return 'MP';
        default:
          return state;
      }
    },
  },
  watch: {
    lead() {
      if (!this.lead) {
        this.$emit('closePopup');
      }
    },
  },
  mounted() {
    if (this.lead) {
      // If lead has a suggested salesperson, no current assignment, use the suggested one
      if (this.lead.SUGGESTEDSALESPERSON?.salespersonid && !this.lead.ASSIGNEDID) {
        this.salesperson = this.lead.SUGGESTEDSALESPERSON?.salespersonid;
        return;
      }

      if (this.lead.ASSIGNEDID) {
        this.salesperson = this.lead.ASSIGNEDID;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.assign-salesperson-popup {
  width: 30rem;

  .popup-body {
    .title {
      font-size: $fontSize16;
      margin: 0;
      display: flex;
      align-items: center;

      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
      img {
        margin-left: 0.5rem;
      }
    }

    .text {
      font-size: $fontSize13;
      margin: 1rem 0;
    }

    .location-note {
      margin-top: 2rem;
      margin-bottom: 0;
    }
  }
}
</style>
